import {get, isString, isFunction, isObject} from 'lodash';
import axios from 'axios';

// Local utils
import crashLogger from 'utils/logger';
import {ERROR_CODES} from '../utils/constants';
import AuthUtils from 'utils/auth';
import {isInElectron, isWebClient} from 'utils/buildMode';
import {isHangryAppMenuPath} from 'utils/menuAvailability';
import {connectOnlineSocket} from 'utils/socket';
import {store} from 'store';

export const REQUEST_ENV = {
	ORDERHUB_API_HOST: isInElectron
		? process.env.REACT_APP_POS_API_HOST
		: (isWebClient ? process.env.REACT_APP_ORDERHUB_API_HOST : process.env.REACT_APP_POS_API_HOST),
	POS_API_HOST: process.env.REACT_APP_POS_API_HOST,
};

export const isRayquazaMenuRoute = () => !isHangryAppMenuPath();

export const withMenuAvailabilityFlags = payload => {
	const requestPayload = {...(payload || {})};
	delete requestPayload.platformLabel;
	if (isWebClient) {
		delete requestPayload.isRayquaza;
		delete requestPayload.hangryAppOnly;
		return requestPayload;
	}
	if (!isRayquazaMenuRoute()) {
		delete requestPayload.isRayquaza;
		return {...requestPayload, hangryAppOnly: true};
	}
	delete requestPayload.hangryAppOnly;
	return {...requestPayload, isRayquaza: true};
};

export const defaultState = {
	loading: false,
	error: null,
};

const withoutLocationIds = value => {
	if (Array.isArray(value)) return value.map(withoutLocationIds);
	if (!value || typeof value !== 'object') return value;
	return Object.fromEntries(Object.entries(value)
		.filter(([key]) => key !== 'locationId')
		.map(([key, entry]) => [key, withoutLocationIds(entry)]));
};

let onlineReauthentication;
const refreshOnlineSession = () => {
	if (!onlineReauthentication) {
		onlineReauthentication = (async () => {
			const credential = await AuthUtils.acquireOnlineCredential();
			const authResponse = await axios.post(`${REQUEST_ENV.POS_API_HOST}/auth/google`, {credential}, {timeout: 30000});
			let loginData = authResponse?.data?.data || authResponse?.data || {};
			if (!loginData.token) throw new Error('Google sign-in did not return a session token.');
			// Admins re-enter at their default location; return them to the one they had switched to.
			const previousLocationId = store.getState().reducerUser?.selectedLocation?.id;
			if (loginData.canSwitchLocation && previousLocationId && previousLocationId !== loginData.location?.id) {
				try {
					const switched = await axios.post(
						`${REQUEST_ENV.POS_API_HOST}/auth/switch-location`,
						{locationId: previousLocationId},
						{timeout: 30000, headers: {Authorization: `Bearer ${loginData.token}`}},
					);
					loginData = switched?.data?.token ? switched.data : loginData;
				} catch {} // Keep the default location if the previous one is no longer accessible.
			}
			const freshToken = loginData.token;
			await AuthUtils.setNewSession(freshToken);
			AuthUtils.setOnlineLocation(loginData.location, loginData.canSwitchLocation);
			connectOnlineSocket(freshToken);
			return freshToken;
		})().finally(() => { onlineReauthentication = null; });
	}
	return onlineReauthentication;
};

axios.interceptors.response.use(response => response, error => {
	const status = error?.response?.status;
	const errorCode = error?.response?.data?.errorCode;
	const message = error?.response?.data?.message || '';
	const url = error?.response?.config?.url || '';
	const skipLogging =
		status === 401 ||
		[
			ERROR_CODES.AUTH_INVALID,
			ERROR_CODES.AUTH_LOCKED,
			ERROR_CODES.AUTH_INACTIVE,
		].includes(errorCode) ||
		(url.includes('/resto/order/process') && message === 'Order already processed');
	if (!skipLogging) {
		let severity = 'error';
		if (
			get(error, 'message') === 'Network Error' ||
			get(error, 'code') === 'ECONNABORTED'
		) {
			severity = 'warning';
		}
		crashLogger.error(error, severity);
	}
	if (!isWebClient && status === 401 && !['/auth', '/admin'].includes(window.location.pathname)) {
		AuthUtils
			.revokeCurrentSession()
			.catch(() => null)
			.finally(() => window.location.reload());
	}
	return Promise.reject(error);
});

const request = async (param1, param2) => {
	const isFunctionBasedComponent = isFunction(param1)
		? param1
		: isFunction(param2)
			? param2
			: null;
	const isClassBasedComponent =
    (isString(get(param1, 'key')) &&
      typeof get(param1, 'component') === 'object') ||
    (isString(get(param2, 'key')) &&
      typeof get(param2, 'component') === 'object');
	const axiosBody =
    isString(get(param1, 'url')) && isString(get(param1, 'method'))
    	? param1
    	: isString(get(param2, 'url')) && isString(get(param2, 'method'))
    		? param2
    		: null;
	const requestConfig = isWebClient && isObject(axiosBody)
		? {
			...axiosBody,
			params: withoutLocationIds(axiosBody.params),
			data: withoutLocationIds(axiosBody.data),
		}
		: axiosBody;
	try {
		if (isFunctionBasedComponent) {
			isFunctionBasedComponent({
				loading: true,
				error: null,
			});
		} else if (isClassBasedComponent) {
			// TODO: class based component stateful request
		}
		const response =
      isObject(axiosBody) &&
      (
      	await axios.request({
      		timeout: 30000,
      		...requestConfig,
      	})
      ).data;
		if (isFunctionBasedComponent) {
			isFunctionBasedComponent({
				loading: false,
				error: null,
			});
		} else if (isClassBasedComponent) {
			// TODO: class based component stateful request
		}
		return response;
	} catch (error) {
		const isOnlineAuthExpired = isWebClient && error?.response?.status === 401 &&
			!error?.config?._onlineAuthRetried &&
			!String(error?.config?.url || '').includes('/auth/google') &&
			!['/auth', '/admin'].includes(window.location.pathname);
		if (isOnlineAuthExpired) {
			try {
				const freshToken = await refreshOnlineSession();
				const retryConfig = {
					...requestConfig,
					_onlineAuthRetried: true,
					headers: {...requestConfig?.headers, Authorization: `Bearer ${freshToken}`},
				};
				const retriedResponse = (await axios.request({timeout: 30000, ...retryConfig})).data;
				if (isFunctionBasedComponent) isFunctionBasedComponent({loading: false, error: null});
				return retriedResponse;
			} catch (reauthError) {
				AuthUtils.revokeCurrentSession();
				if (!['/auth', '/admin'].includes(window.location.pathname)) window.location.assign('/auth?reason=session-expired');
				error.onlineReauthenticationError = reauthError;
			}
		}
		if (isFunctionBasedComponent) {
			isFunctionBasedComponent({
				loading: false,
				error: error,
			});
		} else if (isClassBasedComponent) {
			// TODO: class based component stateful request
		}
		throw error;
	}
};

export default request;
